import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { staticReportLevels } from 'common/enums';
import {
    StaticReportsSnowflakeState,
    Template,
    Scenario,
    StaticReport,
    Filter,
    ReportHistoryItem,
    SnowflakeTable,
    LoadConfig,
    ListLookup
} from './types';

const initialState: StaticReportsSnowflakeState = {
    lookup: {
        taskId: 0,
        releaseGroupId: 0,
        dbName: '',
    },
    scenarios: {
        data: [],
        total: 0,
        filter: {
            pageNumber: 1,
            pageSize: 20,
            searchString: '',
            mergedOnly: true,
            sortingDirection: 2,
            hasInputs: true,
            includeSnowflakeAggregatedStatus: true
        }
    },
    scenariosSelected: [],
    templates: {
        data: [],
        total: 0,
    },
    templatesSelected: [],
    staticReports: {
        data: [],
        total: 0,
    },
    reportsSelected: [],
    snowflakeHistories: {
        data: [],
        total: 0,
        filter: {
            pageNumber: 1,
            pageSize: 50,
            searchString: '',
        }
    },
    masterSpinner: false,
    masterLevel: staticReportLevels.reportHistory,
    editReportModal: {
        open: false,
        title: '',
        spinner: false,
        templates: [],
        item: {
            staticReportName: '',
            description: '',
            query: '',
            templateIds: [],
            global: false,
        }
    },
    editTemplateModal: {
        open: false,
        title: '',
        spinner: false,
        item: {
            templateName: '',
            description: '',
            isGlobal: false,
        },
        staticReports: [],
        linkedReports: [],
        initialLinkedReports: [],
    },
    tables: {
        data: [],
        total: 0
    },
    tablesSelected: [],
    configs: {
        data: [],
        total: 0,
        filter: {
            pageSize: 100,
            pageNumber: 1,
        }
    },
    configsSelected: [],
    editConfigModal: {
        open: false,
        title: '',
        spinner: false,
        item: {
            configId: null,
            configName: '',
            tables: []
        }
    }
};

const staticReportsSnowflakeSlice = createSlice({
    name: 'staticReportsSnowflake',
    initialState,
    reducers: {
        setLookup(state, action: PayloadAction<{ taskId: number; releaseGroupId: number; dbName: string; }>) {
            state.lookup = action.payload;
        },
        setScenarios(state, action: PayloadAction<{ data: Scenario[]; total: number; filter?: Filter }>) {
            state.scenarios.data = action.payload.data;
            state.scenarios.total = action.payload.total;
            state.scenarios.filter = action.payload.filter ? action.payload.filter : state.scenarios.filter;
        },
        setScenariosSelection(state, action: PayloadAction<number[]>) {
            state.scenariosSelected = action.payload;
        },
        setScenariosFilter(state, action: PayloadAction<Filter>) {
            state.scenarios.filter = { ...state.scenarios.filter, ...action.payload };
        },
        setScenariosSnowflakeState(
            state,
            action: PayloadAction<Array<{
                scenarioId: number;
                snowflakeAggregatedStatus?: string;
                tableStatuses?: Record<number, string | null>;
            }>>
        ) {
            for (const upd of action.payload) {
                const scenario = state.scenarios.data.find(s => s.scenarioId === upd.scenarioId);
                if (!scenario) continue;

                if (upd.tableStatuses) {
                    scenario.tableStatuses = upd.tableStatuses;
                } else {
                    delete scenario.tableStatuses;
                }

                if (upd.snowflakeAggregatedStatus) {
                    scenario.snowflakeAggregatedStatus = upd.snowflakeAggregatedStatus;
                }
            }
        },
        setTemplates(state, action: PayloadAction<{ data: Template[]; total: number }>) {
            state.templates = action.payload;
        },
        setTemplateSelection(state, action: PayloadAction<number[]>) {
            state.templatesSelected = action.payload;
        },
        setStaticReports(state, action: PayloadAction<{ data: StaticReport[]; total: number }>) {
            state.staticReports = action.payload;
        },
        setReportSelection(state, action: PayloadAction<number[]>) {
            state.reportsSelected = action.payload;
        },
        setTables(state, action: PayloadAction<{ data: SnowflakeTable[]; total: number }>) {
            state.tables = action.payload;
        },
        setTablesSelected(state, action: PayloadAction<number[]>) {
            state.tablesSelected = action.payload;
        },
        setConfigs(state, action: PayloadAction<{ data: LoadConfig[]; total: number; }>) {
            state.configs = {
                ...action.payload,
                filter: state.configs.filter
            }
        },
        setConfigsSelected(state, action: PayloadAction<number[]>) {
            state.configsSelected = action.payload;
        },
        setBatchCaseFile(state, action: PayloadAction<{ scenarios: { data: Scenario[]; total: number; }, tables: { data: SnowflakeTable[]; total: number }, configs: { data: LoadConfig[]; total: number } }>) {
            state.scenarios = { ...action.payload.scenarios, filter: state.scenarios.filter };
            state.tables = action.payload.tables;
            state.configs = {
                ...action.payload.configs,
                filter: state.configs.filter
            }
        },
        setSnowflakeHistories(state, action: PayloadAction<{ data: ReportHistoryItem[]; total: number; filter?: Filter }>) {
            state.snowflakeHistories.data = action.payload.data;
            state.snowflakeHistories.total = action.payload.total;
            state.snowflakeHistories.filter = action.payload.filter ? action.payload.filter : state.snowflakeHistories.filter;
        },
        setSnowflakeHistoriesFilter(state, action: PayloadAction<Filter>) {
            state.snowflakeHistories.filter = { ...state.snowflakeHistories.filter, ...action.payload };
        },
        changeMasterLevel(state, action: PayloadAction<number>) {
            state.masterLevel = action.payload;
        },
        toggleReportEditSpinner(state, action: PayloadAction<boolean>) {
            state.editReportModal.spinner = action.payload;
        },
        openReportEditModal(
            state,
            action: PayloadAction<{ title: string; item?: Partial<StaticReport>; templates?: Template[]; }>
        ) {
            state.editReportModal.open = true;
            state.editReportModal.title = action.payload.title;
            state.editReportModal.item = {
                staticReportName: action.payload.item?.staticReportName || '',
                description: action.payload.item?.description || '',
                query: action.payload.item?.query || '',
                templateIds: action.payload.item?.templateIds || [],
                global: action.payload.item?.taskId === null || false,
                ...action.payload.item
            };
            state.editReportModal.templates = action.payload.templates || [];
        },
        closeReportEditModal(state) {
            state.editReportModal = { ...initialState.editReportModal };
        },
        changeReportEditModalFields(state, action: PayloadAction<Partial<StaticReport>>) {
            state.editReportModal.item = {
                ...state.editReportModal.item,
                ...action.payload,
            };
        },
        toggleTemplateEditSpinner(state, action: PayloadAction<boolean>) {
            state.editTemplateModal.spinner = action.payload;
        },
        openTemplateEditModal(
            state,
            action: PayloadAction<{ title: string; item?: Partial<Template>; staticReports?: ListLookup[]; linkedReports?: ListLookup[] }>
        ) {
            state.editTemplateModal.open = true;
            state.editTemplateModal.title = action.payload.title;
            state.editTemplateModal.item = {
                templateName: action.payload.item?.templateName || '',
                description: action.payload.item?.description || '',
                isGlobal: action.payload.item?.isGlobal || false,
                ...action.payload.item
            }
            state.editTemplateModal.staticReports = action.payload.staticReports || [];
            state.editTemplateModal.linkedReports = action.payload.linkedReports || [];
            state.editTemplateModal.initialLinkedReports = action.payload.linkedReports || [];
        },
        closeTemplateEditModal(state) {
            state.editTemplateModal = { ...initialState.editTemplateModal };
        },
        setOpenEditConfigModal(state, action: PayloadAction<{ title: string; configName: string; configId: number | null; tables: { id: number; name: string; }[] }>) {
            state.editConfigModal = {
                open: true,
                spinner: false,
                title: action.payload.title,
                item: {
                    configId: action.payload.configId,
                    configName: action.payload.configName,
                    tables: action.payload.tables,
                }
            }
        },
        toggleEditConfigSpinner(state, action: PayloadAction<boolean>) {
            state.editConfigModal.spinner = action.payload;
        },
        closeEditConfigModal(state) {
            state.editConfigModal = initialState.editConfigModal;
        },
        restScenariosFilter(state) {
            state.scenarios.filter = initialState.scenarios.filter;
        },
        resetScreen() {
            return initialState;
        },
    },
});

export const actions = staticReportsSnowflakeSlice.actions;
export default staticReportsSnowflakeSlice.reducer;